import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ConfirmationService, FilterMetadata, MessageService, SortEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { BehaviorSubject, Observable, Subject, take, takeUntil } from 'rxjs';
import { AgencyService } from 'src/app/services/agency.service';
import { ExportsService } from 'src/app/services/exports.service';																 
import { AppConstants } from 'src/app/shared/app.constants';
import { AppUtils } from 'src/app/shared/app.utils';
import { GeographicFilterComponent } from 'src/app/shared/components/geographic-filter/geographic-filter.component';
import { GeographicFilterModel } from 'src/app/shared/models/geographic-filter-model';

@Component({
  selector: 'ness-manage-agency',
  templateUrl: './manage-agency.component.html'
})
export class ManageAgencyComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(GeographicFilterComponent, { static: false }) geoFilterCmp: GeographicFilterComponent;
  @ViewChild('agencydt', { static: false }) agencyTable: Table;

  agencies: ServerModels.UpdateAgencyViewModel[] = [];
  clonedAgencies: { [s: string]: ServerModels.UpdateAgencyViewModel } = {};
  selectedAgencies: ServerModels.UpdateAgencyViewModel[] = [];
  openMergeAgenciesForm: boolean = false;
  displaySidebar: boolean = false;
  cols: any[] = [
    { header: 'Agency Name', field: 'AgencyName' },
    { header: 'CDS', field: 'CDS' },									
    { header: 'ORI Number', field: 'ENESS_XREF_AgencyOriNum.OriNum' },
    { header: 'Field Division', field: 'FieldDivisionName' },
    { header: 'Field Division Group', field: 'ENESS_XREF_AgencyFieldDivisionGroups.GroupName' },
    { header: 'State', field: 'StateName' },
    { header: 'County', field: 'CountyName' }
  ];
  loading: boolean = false;
  readonly geoFilterStorageKey: string = 'geoFilterStorageKeyManageAgency';
  geographicFilter: GeographicFilterModel = null;
  rows: number = AppConstants.defaultTableRows;
  fieldDivisions: BehaviorSubject<ServerModels.ENESS_REF_FieldDivisions[]> = new BehaviorSubject([]);
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.fieldDivisions.asObservable();
  states: BehaviorSubject<ServerModels.ENESS_REF_StateDTO[]> = new BehaviorSubject([]);
  fieldDivisionGroups: BehaviorSubject<ServerModels.ENESS_REF_IntelligenceReportATFReferralGroupsDTO[]> = new BehaviorSubject([]);
  fieldDivisionGroups$: Observable<ServerModels.ENESS_REF_IntelligenceReportATFReferralGroupsDTO[]> = this.fieldDivisionGroups.asObservable();
  states$: Observable<ServerModels.ENESS_REF_StateDTO[]> = this.states.asObservable();
  counties: BehaviorSubject<ServerModels.ENESS_REF_CountiesDTO[]> = new BehaviorSubject([]);
  counties$: Observable<ServerModels.ENESS_REF_CountiesDTO[]> = this.counties.asObservable();
  activeFieldDivisionGroups: { [s: string]: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroupsDTO[] } = {};
  activeStates: { [s: string]: ServerModels.ENESS_REF_StateDTO[] } = {};
  activeCounties: { [s: string]: ServerModels.ENESS_REF_CountiesDTO[] } = {};
  defaultAgencyId: string = "00000000-0000-0000-0000-000000000000";
  selectedAgency: ServerModels.UpdateAgencyViewModel = null;
  agency: ServerModels.UpdateAgencyViewModel = null;
  openMouCdsForm: boolean = false;
  showOnlyOrphan: boolean = false;
  public get ENESS_XREF_AgencyFieldDivisionGroups(): ServerModels.ENESS_XREF_AgencyFieldDivisionGroupsDTO {
    return <ServerModels.ENESS_XREF_AgencyFieldDivisionGroupsDTO>{
      AgencyFieldDivisionGroupID: null,
      AgencyID: null,
      IntelligenceReportATFReferralGroupsID: '',
      GroupName: '',
      CreatedOn: null,
      CreatedBy: null,
      ModifiedOn: null,
      ModifiedBy: null
    };
  }

  private _fieldDivisionStatesCounties: ServerModels.FieldDivisionStateCountyViewModel[] = [];
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(private agencyService: AgencyService,
	private exportService: ExportsService,
	private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private hostElement: ElementRef) {  }

  ngOnInit() {
    this._getFieldDivisionStatesCounties();
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.messageService.clear();
  }

  ngAfterViewInit(): void {
    const userFilterPref: GeographicFilterModel = JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
    if (!!userFilterPref && (userFilterPref['_fieldDivisionID'] !== 'all' || userFilterPref['_stateID'] !== 'all' || userFilterPref['_countyID'] !== 'all' || userFilterPref['_counties'].length > 0)) {
      setTimeout(() => this.applyFilters());
    }
  }

  public addAgencyRow(filterInput: HTMLInputElement): void {
    let emptyVM = this._createEmptyAgencyRow();
    filterInput.value = '';
    this.agencyTable.clearFilterValues();
    if (!!this.agencies.length && !this.agencies[0].AgencyID) {
      this.onRowEditCancel(emptyVM, 0);
    }
    this.agencies = [...[emptyVM], ...this.agencies];
    this.agencyTable.initRowEdit(emptyVM);
  }

  public mergeAgencies(filterInput: HTMLInputElement): void {
    const hasEdits: boolean = !!this.agencyTable.editingRowKeys && Object.keys(this.agencyTable.editingRowKeys).length > 0;
    if (hasEdits) {
      this.confirmMerge();
    } else {
      filterInput.value = null;
      this.displaySidebar = true;
      this.openMergeAgenciesForm = true;
    }
  }

  private _createEmptyAgencyRow(): ServerModels.UpdateAgencyViewModel {
    return {
      AgencyID: this.defaultAgencyId,
      AgencyName: null,
      FieldDivisionID: '',
      FieldDivisionName: '',
      StateID: '',
      StateName: '',
      CountyID: '',
      CountyName: '',
      ENESS_XREF_AgencyOriNum: [
        {
          OriNum: '',
          NIBINAgencyName: ''
        } as ServerModels.ENESS_XREF_AgencyOriNum
      ],
      ENESS_XREF_AgencyFieldDivisionGroups: [
        {
          IntelligenceReportATFReferralGroupsID: ''
        } as ServerModels.ENESS_XREF_AgencyFieldDivisionGroups
      ],
      MOU: false,
      CDS: false,
      CDSApproverID: null,
      DateCDSApproverAssigned: null,
      ENESS_REF_Organization_ID: null,
      OrganizationName: null
    } as ServerModels.UpdateAgencyViewModel;
  }

  public sortMultipleAgency(sortEvent: SortEvent) {
    //sortEvent.data = Data to sort
    //sortEvent.mode = 'single' or 'multiple' sort mode
    //sortEvent.field = Sort field in single sort
    //sortEvent.order = Sort order in single sort
    //sortEvent.multiSortMeta = SortMeta array in multiple sort

    if (sortEvent.mode === 'multiple') {
      sortEvent.data.sort((data1, data2) => {
          return AppUtils.multisortField(data1, data2, sortEvent.multiSortMeta, 0);
        });
    } else {
      this.agencyTable.sort(sortEvent);
    }
  }

  public globalFilterChanged(val) {
    if (!val) { this.agencyTable.clearFilterValues() }
    else { this.agencyTable.filterGlobal(val, 'contains') }
  }

  public filterGlobalAgency(filterEvent: { filters?: FilterMetadata | FilterMetadata[], filteredValue?: any[] }): void {
    if (filterEvent.filters) {
      AppUtils.filterGlobal.apply(this.agencyTable);
    }
  }

  private _getFieldDivisionStatesCounties(): void {
    this.agencyService.getFieldDivisionStateCounties().pipe(take(1)).subscribe(
      (data: ServerModels.FieldDivisionStateCountyViewModel[]) => {
        if (data && !!data.length) {
          this._fieldDivisionStatesCounties = [...data];
          this._setFieldDivisions();
        }
      });
  }

  private _setFieldDivisions(): void {
    if (!this._fieldDivisionStatesCounties.length)
      return;

    let fds = this._fieldDivisionStatesCounties.map(x => x.ENESS_REF_FieldDivisions).sort((a, b) => (a.FieldDivisionName > b.FieldDivisionName) ? 1 : -1);
    this.fieldDivisions.next(fds);
  }

  public onRowEditSave(agency: ServerModels.UpdateAgencyViewModel, index: number): void {
    if (!agency) {
      return;
    }

    let agencyToSave = <ServerModels.ENESS_REF_Agency>{
        AgencyID: agency.AgencyID,
        CreatedOn: agency.CreatedOn,
        CreatedBy: agency.CreatedBy
      };
    agencyToSave.CDS = agency.CDS;
    agencyToSave.MOU = agency.MOU;
    agencyToSave.DateCDSApproverAssigned = agency.DateCDSApproverAssigned;
    agencyToSave.AgencyName = agency.AgencyName;
    agencyToSave.ENESS_XREF_AgencyOriNum = agency.ENESS_XREF_AgencyOriNum.filter(x => !!x.OriNum);
    agencyToSave.FieldDivisionID = agency.FieldDivisionID;
    agencyToSave.CountyID = agency.CountyID;
    agencyToSave.ENESS_XREF_AgencyFieldDivisionGroups = agency.ENESS_XREF_AgencyFieldDivisionGroups.filter(x => !!x.IntelligenceReportATFReferralGroupsID);
    agencyToSave.ENESS_REF_Organization_ID = agency.ENESS_REF_Organization_ID;
    delete agencyToSave.ENESS_REF_FieldDivisions;
    delete agencyToSave.ENESS_REF_Counties;

    this._saveAgency(agencyToSave, index);
  }

  private _saveAgency(agency: ServerModels.ENESS_REF_Agency, index: number): void {
    this.messageService.clear();
    let service$: Observable<ServerModels.UpdateAgencyViewModel> = this.agencyService.addorUpdateAgency(agency);
    service$.pipe(takeUntil(this._unsubscribe$)).subscribe((agencyData: ServerModels.UpdateAgencyViewModel) => {
      if (this.agencyTable.filteredValue && this.agencyTable.filteredValue.length > 0) {
        this.agencyTable.filteredValue[index] = { ...agencyData };
        let srcIndex = this.agencies.findIndex(x => x.AgencyID == agency.AgencyID);
        this.agencies[srcIndex] = { ...agencyData };
      } else {
        this.agencies[index] = { ...agencyData };
      }
      delete this.clonedAgencies[agency.AgencyID];
      delete this.activeFieldDivisionGroups[agency.AgencyID];
      delete this.activeStates[agency.AgencyID];
      delete this.activeCounties[agency.AgencyID];
      },
      (err: any) => {
        let agencyVM: ServerModels.UpdateAgencyViewModel = <ServerModels.UpdateAgencyViewModel>{
          AgencyID: agency.AgencyID,
          AgencyName: agency.AgencyName,
          ENESS_XREF_AgencyOriNum: agency.ENESS_XREF_AgencyOriNum || [],
          FieldDivisionID: agency.FieldDivisionID,
          ENESS_XREF_AgencyFieldDivisionGroups: agency.ENESS_XREF_AgencyFieldDivisionGroups || [],
          CountyID: agency.CountyID,
          StateID: !!agency.ENESS_REF_Counties ? agency.ENESS_REF_Counties.StateID : null
        };
        this.onRowEditCancel(agencyVM, index);
        let msgDetail = err.error?.ExceptionMessage || err.message || 'An error occurred while updating the agency. The agency changes were not saved.';
        this.messageService.add({ key: 'manageAgenciesMsg', severity: 'error', summary: 'Error', detail: msgDetail });
      }
    );
  }

  public onRowEditCancel(agency: ServerModels.UpdateAgencyViewModel, index: number): void {
    if (!!agency.AgencyID && agency.AgencyID != this.defaultAgencyId) {
      if (this.agencyTable.filteredValue) {
        let agenciesIndex: number = this.agencies.indexOf(agency);
        this.agencies[agenciesIndex] = this.clonedAgencies[agency.AgencyID];
        this.agencyTable.filteredValue[index] = this.clonedAgencies[agency.AgencyID];
      } else {
        this.agencies[index] = this.clonedAgencies[agency.AgencyID];
      }
      agency.AgencyName = this.clonedAgencies[agency.AgencyID].AgencyName;
      agency.CountyID = this.clonedAgencies[agency.AgencyID].CountyID;
      agency.CountyName = this.clonedAgencies[agency.AgencyID].CountyName;
      agency.StateID = this.clonedAgencies[agency.AgencyID].StateID;
      agency.StateName = this.clonedAgencies[agency.AgencyID].StateName;
      agency.FieldDivisionID = this.clonedAgencies[agency.AgencyID].FieldDivisionID;
      agency.FieldDivisionName = this.clonedAgencies[agency.AgencyID].FieldDivisionName;
      agency.CDS = this.clonedAgencies[agency.AgencyID].CDS;
      agency.MOU = this.clonedAgencies[agency.AgencyID].MOU;
      agency.DateCDSApproverAssigned = this.clonedAgencies[agency.AgencyID].DateCDSApproverAssigned;
      agency.CDSApproverID = this.clonedAgencies[agency.AgencyID].CDSApproverID;
      let newOris = this.clonedAgencies[agency.AgencyID].ENESS_XREF_AgencyOriNum.filter(x => x.AgencyOriNumID == null);
      newOris.forEach(
        (ori, idx) => {
          agency.ENESS_XREF_AgencyOriNum.splice(this.clonedAgencies[agency.AgencyID].ENESS_XREF_AgencyOriNum.indexOf(ori), 1);
        });
      let newFDGroups = this.clonedAgencies[agency.AgencyID].ENESS_XREF_AgencyFieldDivisionGroups.filter(x => x.AgencyFieldDivisionGroupID == null);
      newFDGroups.forEach(
        (fdg, idx) => {
          agency.ENESS_XREF_AgencyFieldDivisionGroups.splice(this.clonedAgencies[agency.AgencyID].ENESS_XREF_AgencyFieldDivisionGroups.indexOf(fdg), 1);
        });
      delete this.clonedAgencies[agency.AgencyID];
      delete this.activeFieldDivisionGroups[agency.AgencyID];
      delete this.activeStates[agency.AgencyID];
      delete this.activeCounties[agency.AgencyID];
    } else {
      this.agencyTable.cancelRowEdit(agency);
      this._deleteRow(index);
    }
  }

  public addNewORINumberRow(agency: ServerModels.UpdateAgencyViewModel): void {
    if (!AppUtils.objectIsEmpty(agency)) {
      let newOri = {
        AgencyID: agency.AgencyID.toUpperCase(),
        OriNum: '',
        NIBINAgencyName: ''
      } as ServerModels.ENESS_XREF_AgencyOriNum
      agency.ENESS_XREF_AgencyOriNum.push(newOri);
    }
  }

  public deleteNewORINumberRow(agency: ServerModels.ENESS_REF_Agency, index: number): void {
    if (index && !AppUtils.objectIsEmpty(agency)) {
      agency.ENESS_XREF_AgencyOriNum.splice(index, 1);
    }
  }

  public addNewFieldDivisionGroupRow(agency: ServerModels.UpdateAgencyViewModel): void {
    if (!AppUtils.objectIsEmpty(agency)) {
      let newFDGroup = this.ENESS_XREF_AgencyFieldDivisionGroups;
      newFDGroup.AgencyFieldDivisionGroupID = null;
      newFDGroup.AgencyID = agency.AgencyID.toUpperCase();
      newFDGroup.IntelligenceReportATFReferralGroupsID = '';
      newFDGroup.GroupName = '';

      agency.ENESS_XREF_AgencyFieldDivisionGroups.push(newFDGroup);
    }
  }

  public deleteNewFieldDivisionGroupRow(agency: ServerModels.UpdateAgencyViewModel, index: number): void {
    if (index !== null && !AppUtils.objectIsEmpty(agency)) {
      agency.ENESS_XREF_AgencyFieldDivisionGroups.splice(index, 1);
    }
  }

  public updateFieldDivisionGroupRow(fdGroup: ServerModels.ENESS_XREF_AgencyFieldDivisionGroupsDTO, groupId: string): void {
    if (!!groupId && !AppUtils.objectIsEmpty(fdGroup)) {
      let fieldDivisionGroupObj: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroupsDTO = this.fieldDivisionGroups.getValue().find((p: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroupsDTO) => p.IntelligenceReportATFReferralGroupsID.toLowerCase() === groupId.toLowerCase());
      if (!AppUtils.objectIsEmpty(fieldDivisionGroupObj)) {
        fdGroup.IntelligenceReportATFReferralGroupsID = fieldDivisionGroupObj.IntelligenceReportATFReferralGroupsID;
        fdGroup.GroupName = fieldDivisionGroupObj.GroupName; // set DTO bound field
      }
    }
  }

  private _deleteRow(rowIndex: number) {
    this.agencies = this.agencies.filter((val, i) => i != rowIndex);
  }

  public onRowEditInit(agency: ServerModels.UpdateAgencyViewModel): void {
    if (!!agency.AgencyID) {
      this.cloneAgency(agency);
    }

    if (!agency.FieldDivisionID) {
      agency.CountyID = '';
      agency.ENESS_XREF_AgencyFieldDivisionGroups = [];
    } else if (!agency.ENESS_XREF_AgencyFieldDivisionGroups || agency.ENESS_XREF_AgencyFieldDivisionGroups.length == 0) {
      let emptyFDGroup = this.ENESS_XREF_AgencyFieldDivisionGroups;
      emptyFDGroup.AgencyID = agency.AgencyID;
      agency.ENESS_XREF_AgencyFieldDivisionGroups = [emptyFDGroup];
    }

    this._setFieldDivisionDependencies(agency);
    this._setCountiesForState(agency);
  };

  public cloneAgency(agency: ServerModels.UpdateAgencyViewModel) {
    let clonedOris = [] as ServerModels.ENESS_XREF_AgencyOriNum[];
    agency.ENESS_XREF_AgencyOriNum.forEach(
      (ori, idx) => {
        let newOri = {
          AgencyID: ori.AgencyID,
          AgencyOriNumID: ori.AgencyOriNumID,
          CreatedBy: ori.CreatedBy,
          CreatedOn: ori.CreatedOn,
          Deleted: ori.Deleted,
          ENESS_REF_Agency: ori.ENESS_REF_Agency,
          ModifiedBy: ori.ModifiedBy,
          ModifiedOn: ori.ModifiedOn,
          OriNum: ori.OriNum,
          NIBINAgencyName: ori.NIBINAgencyName
        } as ServerModels.ENESS_XREF_AgencyOriNum

        clonedOris.push(newOri)
      });

    let clonedFDGroups = [] as ServerModels.ENESS_XREF_AgencyFieldDivisionGroupsDTO[];
    agency.ENESS_XREF_AgencyFieldDivisionGroups?.forEach(
      (fdg, idx) => {
        let newFDG = <ServerModels.ENESS_XREF_AgencyFieldDivisionGroupsDTO>{
          AgencyFieldDivisionGroupID: fdg.AgencyFieldDivisionGroupID,
          AgencyID: fdg.AgencyID,
          IntelligenceReportATFReferralGroupsID: fdg.IntelligenceReportATFReferralGroupsID,
          GroupName: fdg.GroupName,
          CreatedOn: fdg.CreatedOn,
          CreatedBy: fdg.CreatedBy,
          ModifiedOn: fdg.ModifiedOn,
          ModifiedBy: fdg.ModifiedBy
        };

        clonedFDGroups.push(newFDG);
      });

    let tempAg = {
      AgencyID: agency.AgencyID,
      AgencyName: agency.AgencyName,
      FieldDivisionID: agency.FieldDivisionID,
      FieldDivisionName: agency.FieldDivisionName,
      StateID: agency.StateID,
      StateName: agency.StateName,
      CountyID: agency.CountyID,
      CountyName: agency.CountyName,
      ENESS_REF_Organization_ID: agency.ENESS_REF_Organization_ID,
      ENESS_XREF_AgencyOriNum: clonedOris,
      ENESS_XREF_AgencyFieldDivisionGroups: clonedFDGroups,
      CDS: agency.CDS,
      MOU: agency.MOU,
      DateCDSApproverAssigned: agency.DateCDSApproverAssigned,
      CDSApproverID: agency.CDSApproverID
    } as ServerModels.UpdateAgencyViewModel;

    this.clonedAgencies[agency.AgencyID] = Object.assign({}, tempAg);
  }

  public onSelectFieldDivision(agency: ServerModels.UpdateAgencyViewModel): void {
    this._setFieldDivisionDependencies(agency);
    const selectedFieldDivision = this.fieldDivisions.getValue().find((x: ServerModels.ENESS_REF_FieldDivisions) => x.FieldDivisionID == agency.FieldDivisionID);
    agency.FieldDivisionID = selectedFieldDivision == null ? null : selectedFieldDivision.FieldDivisionID;
    let emptyFDGroup = this.ENESS_XREF_AgencyFieldDivisionGroups;
    emptyFDGroup.AgencyID = agency.AgencyID;
    agency.ENESS_XREF_AgencyFieldDivisionGroups = [emptyFDGroup];
    agency.StateID = '';
    agency.CountyID = '';
  }

  private _setFieldDivisionDependencies(agency: ServerModels.UpdateAgencyViewModel): void {
    if (!this._fieldDivisionStatesCounties.length) {
      return;
    }

    let fdGroups: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroupsDTO[] = [];
    let states: ServerModels.ENESS_REF_StateDTO[] = [];
    let counties: ServerModels.ENESS_REF_CountiesDTO[] = [];
    for (let i = 0; i < this._fieldDivisionStatesCounties.length; i++) {
      if (this._fieldDivisionStatesCounties[i].FieldDivisionID == agency.FieldDivisionID) {
        fdGroups = [...this._fieldDivisionStatesCounties[i].FieldDivisionGroups];
        states = [...this._fieldDivisionStatesCounties[i].StatesForCGIC];
        counties = [...this._fieldDivisionStatesCounties[i].CountiesForCGICState];
        fdGroups.sort((a, b) => (a.GroupName > b.GroupName) ? 1 : -1);
        counties.sort((a, b) => (a.CountyName > b.CountyName) ? 1 : -1);
        states.sort((a, b) => (a.StateName > b.StateName) ? 1 : -1);
      }
    }

    this.fieldDivisionGroups.next([...fdGroups]);
    this.activeFieldDivisionGroups[agency.AgencyID] = fdGroups;
    this.states.next([...states]);
    this.activeStates[agency.AgencyID] = states;
    this.counties.next([...counties]);
    this.activeCounties[agency.AgencyID] = counties;
  }

  public onSelectState(agency: ServerModels.UpdateAgencyViewModel): void {
    this._setCountiesForState(agency);
    agency.CountyID = '';
  }

  private _setCountiesForState(agency: ServerModels.UpdateAgencyViewModel): void {
    if (!this._fieldDivisionStatesCounties.length)
      return;

    let counties: ServerModels.ENESS_REF_Counties[] = [];
    for (let i = 0; i < this._fieldDivisionStatesCounties.length; i++) {
      if (this._fieldDivisionStatesCounties[i].FieldDivisionID == agency.FieldDivisionID) {
        let stateCounties = this._fieldDivisionStatesCounties[i].CountiesForCGICState.filter(x => x.StateID == agency.StateID);
        counties = [...stateCounties];
        counties.sort((a, b) => (a.CountyName > b.CountyName) ? 1 : -1);
      }
    }
    this.counties.next([...counties]);
    this.activeCounties[agency.AgencyID] = counties;
  }

  public applyFilters(): void {
    this._setGeographicFilter();
  }

  private _setGeographicFilter(): void {
    this.geoFilterCmp.applyFilters();
    let storedGeoFilterModel = JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
    this.geographicFilter = new GeographicFilterModel(storedGeoFilterModel["_fieldDivisionID"],
      storedGeoFilterModel['_fieldDivisionGroupID'],
      storedGeoFilterModel['_fieldDivisionGroups'],
      storedGeoFilterModel["_stateID"],
      storedGeoFilterModel["_countyID"],
      storedGeoFilterModel["_counties"],
      storedGeoFilterModel['_agencyID'],
      storedGeoFilterModel['_agencies'],
      storedGeoFilterModel["_fieldDivisionName"],
      storedGeoFilterModel['_groupName'],
      storedGeoFilterModel['_groupNameList'],
      storedGeoFilterModel["_stateName"],
      storedGeoFilterModel["_countyName"],
      storedGeoFilterModel["_countyNameList"],
      storedGeoFilterModel['_agencyName'],
      storedGeoFilterModel['_agencyNamesList']);

    this.agencies = [];
    this._getAgenciesList();
  }

  private _getAgenciesList(): void {
    let filtersVM = {
      AgencyName: null,
      FieldDivisionID: null,
      FieldDivisionGroupIDs: null,
      StateID: null,
      CountyIDs: null,
      AgencyIDs: null,
      StartDate: null,
      EndDate: null,
      PageNumber: 0,
      RowsPerPage: AppConstants.dbPagingDefaultRows
    } as ServerModels.FiltersViewModel;

    filtersVM.FieldDivisionID = this.geographicFilter.fieldDivisionID;
    filtersVM.FieldDivisionGroupIDs = this.geographicFilter.fieldDivisionGroups.map(x => x.IntelligenceReportATFReferralGroupsID);
    filtersVM.StateID = this.geographicFilter.stateID;
    filtersVM.CountyIDs = this.geographicFilter.counties.map(x => x.CountyID);
    filtersVM.AgencyIDs = this.geographicFilter.agencies.map(x => x.AgencyID);
    this.loading = true;
    this.agencyService.getAllAgenciesToUpdate(this.showOnlyOrphan, filtersVM).pipe(take(1)).subscribe(
      (data: ServerModels.UpdateAgencyViewModel[]) => {
        this.loading = false;
        if (data && !!data.length)
          this.agencies = [...data];
      },
      (err: any) => this.loading = false
    );
  }

  public resetDefaultFilters(): void {
    if (!!this.geoFilterCmp) {
      this.showOnlyOrphan = false;
      this.geoFilterCmp.resetToDefaultFilters();
    }
  }

  public disableSaveAgency(agency: ServerModels.UpdateAgencyViewModel) {
    let disable = false;
    // Disable save when conditions are not met.
    if (!agency.AgencyName || !agency.FieldDivisionID || !agency.StateID || !agency.CountyID || !agency.ENESS_XREF_AgencyOriNum[0]?.OriNum || !agency.ENESS_XREF_AgencyFieldDivisionGroups[0]?.IntelligenceReportATFReferralGroupsID) {
      disable = true;
    }
    // Disable Save if an existing Ori number is removed 
    if (agency.ENESS_XREF_AgencyOriNum.filter(x => x.OriNum == '' && x.AgencyOriNumID != '').length > 0) {
      disable = true;
    }

    if (agency.ENESS_XREF_AgencyFieldDivisionGroups.filter(x => !x.IntelligenceReportATFReferralGroupsID && !x.AgencyFieldDivisionGroupID).length > 0) {
      disable = true;
    }

    return disable;
  }

  public disableMergeCheckbox(agency: ServerModels.ENESS_REF_Agency) {
    if (this.selectedAgencies.length >= 2) {
      if (agency.AgencyID === this.selectedAgencies[0].AgencyID) {
        return false;
      }
      if (agency.AgencyID === this.selectedAgencies[1].AgencyID) {
        return false;
      }
      return true;
    }
    return false;
  }

  public onShowSidebar(): void {
    let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
    if (targetEl.length) {
      targetEl[0].scrollTop = 0;
      targetEl[0].setAttribute("tabindex", "-1");
      targetEl[0].focus();
    }
  }

  public onHideSidebar(): void {
    this.openMergeAgenciesForm = false;
    this.openMouCdsForm = false;
    this.displaySidebar = false;
  }

  // public onMergeFormClose(): void {
  //   this.openMergeAgenciesForm = false;
  //   this.displaySidebar = false;
  // }

  public mergeCompleted(): void {
    this.selectedAgencies = [];
    this.applyFilters();
    this.onHideSidebar();
  }

  public confirmMerge() {
    this.confirmationService.confirm({
      message: 'One or more agency rows has unsaved changes. Please either save your changes or cancel editing before merging.',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Ok',
      rejectVisible: false
    });
  }

  public viewCDSDetails(data: ServerModels.UpdateAgencyViewModel) {
    if (data) {
      // Clone the agency data for CDS view page
      this.agency = Object.assign({}, data);
    }
    this.displaySidebar = true;
    this.openMouCdsForm = true;
  }

  public onMouCdsSave(): void {
    this.selectedAgency = null;
    this.agency = null;
    this.applyFilters();
    this.onHideSidebar();
  }
  public exportCdsData(filterInput: HTMLInputElement): void {
    let filtersVM = {
      AgencyName: null,
      FieldDivisionID: null,
      FieldDivisionGroupIDs: null,
      StateID: null,
      CountyIDs: null,
      AgencyIDs: null,
      StartDate: null,
      EndDate: null,
      PageNumber: 0,
      RowsPerPage: AppConstants.dbPagingDefaultRows
    } as ServerModels.FiltersViewModel;

    filtersVM.FieldDivisionID = this.geographicFilter.fieldDivisionID;
    filtersVM.FieldDivisionGroupIDs = this.geographicFilter.fieldDivisionGroups.map(x => x.IntelligenceReportATFReferralGroupsID);
    filtersVM.StateID = this.geographicFilter.stateID;
    filtersVM.CountyIDs = this.geographicFilter.counties.map(x => x.CountyID);
    filtersVM.AgencyIDs = this.geographicFilter.agencies.map(x => x.AgencyID);
    this.loading = true;
    this.exportService.getAgencyCDSMouExport(filtersVM).pipe(take(1)).subscribe(
      (result: ServerModels.ENESS_Exports) => {
        this.loading = false;
        if (result.CSVFile) {
          let options = {
            fileName: `AgencyCDSMouExport.csv`
          };
          AppUtils.exportBlobToCsv(result.CSVFile, options);
        }
      },
      (err: any) => this.loading = false
    );
  }
}

